import React, { useState, useEffect } from 'react';
import { API, getToken } from './adminApi';

const STATUS_COLOUR = { draft: '#94a3b8', pending: '#f59e0b', approved: '#22c55e', rejected: '#ef4444', in_review: '#6366f1' };

// "12s" / "3m 05s" between the demo opening and the signup click.
const fmtGap = (from, to) => {
  const s = Math.max(0, Math.round((new Date(to) - new Date(from)) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

const ActivityTab = () => {
  const [activity, setActivity] = useState(null);
  const [activityDays, setActivityDays] = useState(7);
  const [activityLoading, setActivityLoading] = useState(false);
  const [expandedDemo, setExpandedDemo] = useState(null);
  const [fetchError, setFetchError] = useState('');
  // Admin and local test chats are hidden unless this is on (remembered).
  const [includeInternal, setIncludeInternal] = useState(() => {
    try { return localStorage.getItem('adminActivityIncludeInternal') === '1'; } catch { return false; }
  });
  const toggleInternal = () => setIncludeInternal(v => {
    try { localStorage.setItem('adminActivityIncludeInternal', v ? '0' : '1'); } catch { /* ignore */ }
    return !v;
  });

  useEffect(() => { fetchActivity(); }, [activityDays, includeInternal]);

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/activity?days=${activityDays}${includeInternal ? '&includeInternal=1' : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setActivity(data);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setActivityLoading(false);
    }
  };

  // Mark a chat as admin / test (or not): hidden unless "Show admin & test
  // chats" is on — and that browser's other chats with it.
  const markChat = async (kind, chat) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/activity/mark`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, id: chat._id, internal: !chat.adminMarked }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed');
      fetchActivity();
    } catch (err) {
      setFetchError(`Couldn't mark that chat (${err.message})`);
    }
  };
  const MarkButton = ({ kind, chat }) => (
    <button type="button" className="as-filter-tab" style={{ marginBottom: '0.5rem' }} onClick={() => markChat(kind, chat)}>
      {chat.adminMarked ? 'Unmark (not admin/test)' : 'Mark as admin/test'}
    </button>
  );

  return (
    <div className="as-activity">
      {fetchError && <div className="as-error">{fetchError}</div>}
      <div className="as-activity-toolbar">
        {[1, 3, 7, 14, 30].map(d => (
          <button key={d} className={`as-filter-tab ${activityDays === d ? 'as-filter-tab--active' : ''}`} onClick={() => setActivityDays(d)}>{d}d</button>
        ))}
        <button className="as-filter-tab" onClick={fetchActivity} style={{ marginLeft: '0.5rem' }}>↻ Refresh</button>
        <label style={{ marginLeft: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={includeInternal} onChange={toggleInternal} />
          Show admin &amp; test chats
        </label>
      </div>

      {activityLoading && <div className="as-loading">Loading…</div>}

      {activity && activity.builders && activity.demos && (<>
        <h2 className="as-section-title">🏗️ Community Builder ({activity.builders.length})</h2>
        <div className="as-act-table-wrap">
          <table className="as-act-table">
            <thead><tr><th>Time</th><th>Status</th><th>Who</th><th>Community</th></tr></thead>
            <tbody>
              {activity.builders.length === 0 && <tr><td colSpan="4" className="as-act-empty">None</td></tr>}
              {activity.builders.map(b => (
                <tr key={b._id}>
                  <td className="as-act-time">{new Date(b.createdAt).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</td>
                  <td><span className="as-act-badge" style={{ background: STATUS_COLOUR[b.status] || '#ccc' }}>{b.status}</span></td>
                  <td className="as-act-who">{b.submittedByEmail && b.submittedByEmail !== 'anon' ? b.submittedByEmail : `anon:${(b.sessionId||'').slice(0,8)}`}</td>
                  <td><strong>{b.title}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {activity.demoChats && (<>
          <h2 className="as-section-title" style={{ marginTop: '1.5rem' }}>💬 Demo chats — /meet ({activity.demoChats.length})</h2>
          <div className="as-act-table-wrap">
            <table className="as-act-table">
              <thead><tr><th>Time</th><th>Community</th><th>Replies</th><th>Reached</th><th>Last message</th></tr></thead>
              <tbody>
                {activity.demoChats.length === 0 && <tr><td colSpan="5" className="as-act-empty">None</td></tr>}
                {activity.demoChats.map(d => {
                  const isOpen = expandedDemo === d._id;
                  return (
                    <React.Fragment key={d._id}>
                      <tr
                        style={{ background: d.replies > 2 ? '#f0fdf4' : 'transparent', cursor: 'pointer' }}
                        onClick={() => setExpandedDemo(isOpen ? null : d._id)}
                      >
                        <td className="as-act-time">{new Date(d.createdAt).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</td>
                        <td>
                          {d.communityName}
                          {d.internal && <span className="as-act-badge" style={{ background: '#94a3b8', marginLeft: '0.4rem' }}>admin/test</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>{d.replies}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{d.signedUp ? '✅ signed up' : d.reachedSignup ? 'sign-up box' : d.stage}</td>
                        <td className="as-act-lastmsg">{d.lastUserMessage || '—'} <span style={{ color:'#6366f1', fontSize:'0.75rem' }}>{isOpen ? '▲' : '▼'}</span></td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan="5" className="as-demo-thread">
                            <MarkButton kind="meet" chat={d} />
                            {(d.messages || []).map((m, i) => (
                              <div key={i} className={`as-demo-msg as-demo-msg--${m.role}`}>
                                <span className="as-demo-role">{m.role === 'user' ? '👤' : '🤖'}</span>
                                <span className="as-demo-content">{m.content}</span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>)}

        <h2 className="as-section-title" style={{ marginTop: '1.5rem' }}>🤖 AI Try Now Sessions (old demo) ({activity.demos.length})</h2>
        <div className="as-act-table-wrap">
          <table className="as-act-table">
            <thead><tr><th>Time</th><th>Community</th><th>Msgs</th><th>Signup</th><th>Last message</th></tr></thead>
            <tbody>
              {activity.demos.length === 0 && <tr><td colSpan="5" className="as-act-empty">None</td></tr>}
              {activity.demos.map(d => {
                const isOpen = expandedDemo === d._id;
                return (
                  <React.Fragment key={d._id}>
                    <tr
                      style={{ background: d.messageCount > 2 ? '#f0fdf4' : 'transparent', cursor: 'pointer' }}
                      onClick={() => setExpandedDemo(isOpen ? null : d._id)}
                    >
                      <td className="as-act-time">{new Date(d.createdAt).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</td>
                      <td>
                        {d.communityName}
                        {d.internal && <span className="as-act-badge" style={{ background: '#94a3b8', marginLeft: '0.4rem' }}>admin/test</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>{d.messageCount}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {d.reachedSignup ? '✅' : '—'}
                        {d.signupClickFrom?.includes('first_message') && <div className="as-card-meta">from first message</div>}
                        {d.signupClickedAt && <div className="as-card-meta">after {fmtGap(d.createdAt, d.signupClickedAt)}</div>}
                      </td>
                      <td className="as-act-lastmsg">{d.lastUserMessage || '—'} <span style={{ color:'#6366f1', fontSize:'0.75rem' }}>{isOpen ? '▲' : '▼'}</span></td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan="5" className="as-demo-thread">
                          <MarkButton kind="demo" chat={d} />
                          {(d.messages || []).map((m, i) => (
                            <div key={i} className={`as-demo-msg as-demo-msg--${m.role}`}>
                              <span className="as-demo-role">{m.role === 'user' ? '👤' : '🤖'}</span>
                              <span className="as-demo-content">{m.content}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
};

export default ActivityTab;
